import React, { useState } from 'react';
import { 
  Database, 
  Activity, 
  Brain, 
  Zap,
  BarChart3,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  Clock,
  Target,
  Eye,
  Search,
  Filter,
  Layers,
  GitBranch,
  Cpu
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, ScatterChart, Scatter } from 'recharts';

const DataDashboard: React.FC = () => {
  const [selectedTimeframe, setSelectedTimeframe] = useState('weekly');
  const [selectedDataSource, setSelectedDataSource] = useState('all');

  // Data quality and coverage metrics
  const dataQualityData = [
    { week: 'W1', completeness: 94, accuracy: 92, consistency: 89, timeliness: 96, coverage: 88 },
    { week: 'W2', completeness: 96, accuracy: 94, consistency: 91, timeliness: 95, coverage: 90 },
    { week: 'W3', completeness: 93, accuracy: 91, consistency: 88, timeliness: 97, coverage: 89 },
    { week: 'W4', completeness: 97, accuracy: 95, consistency: 93, timeliness: 94, coverage: 92 },
    { week: 'W5', completeness: 95, accuracy: 93, consistency: 90, timeliness: 98, coverage: 91 },
    { week: 'W6', completeness: 98, accuracy: 96, consistency: 94, timeliness: 96, coverage: 94 },
  ];

  const insightVelocityData = [
    { month: 'Jan', generated: 45, validated: 32, acted_upon: 18, impact_score: 72 },
    { month: 'Feb', generated: 52, validated: 38, acted_upon: 22, impact_score: 78 },
    { month: 'Mar', generated: 48, validated: 35, acted_upon: 20, impact_score: 75 },
    { month: 'Apr', generated: 58, validated: 42, acted_upon: 28, impact_score: 82 },
    { month: 'May', generated: 62, validated: 45, acted_upon: 32, impact_score: 85 },
    { month: 'Jun', generated: 67, validated: 48, acted_upon: 35, impact_score: 88 },
  ];

  const dataSourcesData = [
    { source: 'User Analytics', volume: 2.4, quality: 96, latency: 2, coverage: 94, color: '#3B82F6' },
    { source: 'Transaction Data', volume: 1.8, quality: 98, latency: 1, coverage: 98, color: '#10B981' },
    { source: 'Marketing Data', volume: 1.2, quality: 92, latency: 5, coverage: 89, color: '#F59E0B' },
    { source: 'Support Data', volume: 0.8, quality: 89, latency: 8, coverage: 85, color: '#8B5CF6' },
    { source: 'External APIs', volume: 0.6, quality: 85, latency: 12, coverage: 78, color: '#EF4444' },
  ];

  const modelPerformanceData = [
    { model: 'Recommendation Engine', accuracy: 94, precision: 92, recall: 89, f1_score: 90, drift: 2.1 },
    { model: 'Churn Prediction', accuracy: 87, precision: 85, recall: 88, f1_score: 86, drift: 3.8 },
    { model: 'Fraud Detection', accuracy: 96, precision: 94, recall: 92, f1_score: 93, drift: 1.5 },
    { model: 'Price Optimization', accuracy: 91, precision: 89, recall: 93, f1_score: 91, drift: 2.9 },
    { model: 'Sentiment Analysis', accuracy: 88, precision: 86, recall: 90, f1_score: 88, drift: 4.2 },
  ];

  // Key data metrics
  const dataMetrics = [
    {
      id: 'data_quality_index',
      name: 'Data Quality Index',
      value: 94,
      change: 2.1,
      positive: true,
      description: 'Overall data quality across all sources',
      target: 95,
      unit: '%',
      volume: '12.8TB processed'
    },
    {
      id: 'insight_velocity',
      name: 'Insight Velocity',
      value: 67,
      change: 8.1,
      positive: true,
      description: 'Rate of insight generation and validation',
      target: 60,
      unit: '/month',
      volume: '35 acted upon'
    },
    {
      id: 'model_accuracy',
      name: 'Model Accuracy',
      value: 91.2,
      change: 1.8,
      positive: true,
      description: 'Average accuracy across all ML models',
      target: 90,
      unit: '%',
      volume: '8 models active'
    },
    {
      id: 'data_coverage',
      name: 'Signal Coverage',
      value: 89,
      change: 3.2,
      positive: true,
      description: 'Percentage of business signals captured',
      target: 85,
      unit: '%',
      volume: '247 signals tracked'
    }
  ];

  // Data pipeline health
  const pipelineHealth = [
    { pipeline: 'User Events Pipeline', status: 'healthy', throughput: '2.4M/hour', latency: '< 2s', errors: 0.01 },
    { pipeline: 'Transaction Pipeline', status: 'healthy', throughput: '180K/hour', latency: '< 1s', errors: 0.005 },
    { pipeline: 'Marketing Pipeline', status: 'warning', throughput: '120K/hour', latency: '< 5s', errors: 0.08 },
    { pipeline: 'ML Feature Pipeline', status: 'healthy', throughput: '500K/hour', latency: '< 3s', errors: 0.02 },
    { pipeline: 'Analytics Pipeline', status: 'critical', throughput: '80K/hour', latency: '< 15s', errors: 0.15 },
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy': return 'text-green-400 bg-green-900 border-green-600';
      case 'warning': return 'text-yellow-400 bg-yellow-900 border-yellow-600';
      case 'critical': return 'text-red-400 bg-red-900 border-red-600';
      default: return 'text-gray-400 bg-gray-900 border-gray-600';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy': return <CheckCircle className="w-4 h-4" />;
      case 'warning': return <AlertTriangle className="w-4 h-4" />;
      case 'critical': return <AlertTriangle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2 flex items-center space-x-3">
            <Database className="w-8 h-8 text-cyan-400" />
            <span>Data & Analytics Dashboard</span>
          </h1>
          <p className="text-gray-400">Data quality, insight velocity, and analytics performance</p>
        </div>
        <div className="flex items-center space-x-3 mt-4 lg:mt-0">
          <select
            value={selectedTimeframe}
            onChange={(e) => setSelectedTimeframe(e.target.value)}
            className="bg-gray-800 text-white px-4 py-2 rounded-lg border border-gray-700 focus:border-indigo-500 focus:outline-none"
          >
            <option value="daily">Daily View</option>
            <option value="weekly">Weekly View</option>
            <option value="monthly">Monthly View</option>
          </select>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-cyan-500 rounded-full animate-pulse"></div>
            <span className="text-sm text-gray-300">Real-time Data Flow</span>
          </div>
        </div>
      </div>

      {/* Key Data Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {dataMetrics.map((metric) => (
          <div key={metric.id} className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-gray-300">{metric.name}</h3>
              {metric.positive ? (
                <TrendingUp className="w-5 h-5 text-green-500" />
              ) : (
                <TrendingUp className="w-5 h-5 text-red-500 transform rotate-180" />
              )}
            </div>
            <div className="flex items-baseline space-x-2 mb-2">
              <span className="text-2xl font-bold text-white">{metric.value}</span>
              <span className="text-sm text-gray-400">{metric.unit}</span>
            </div>
            <div className="flex items-center justify-between mb-3">
              <span className={`text-sm ${metric.positive ? 'text-green-500' : 'text-red-500'}`}>
                {metric.positive ? '+' : ''}{metric.change}%
              </span>
              <span className="text-xs text-gray-400">{metric.volume}</span>
            </div>
            <div className="w-full bg-gray-700 rounded-full h-2">
              <div 
                className={`h-2 rounded-full ${metric.value >= metric.target ? 'bg-green-500' : 'bg-yellow-500'}`}
                style={{ width: `${Math.min((metric.value / (metric.target * 1.2)) * 100, 100)}%` }}
              ></div>
            </div>
          </div>
        ))}
      </div>

      {/* Data Quality & Insight Velocity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Data Quality Trends */}
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
            <CheckCircle className="w-5 h-5 text-green-400" />
            <span>Data Quality Trends</span>
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={dataQualityData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="week" stroke="#9CA3AF" />
              <YAxis stroke="#9CA3AF" />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1F2937',
                  border: '1px solid #374151',
                  borderRadius: '8px',
                  color: '#F9FAFB'
                }}
              />
              <Line type="monotone" dataKey="completeness" stroke="#10B981" strokeWidth={2} />
              <Line type="monotone" dataKey="accuracy" stroke="#3B82F6" strokeWidth={2} />
              <Line type="monotone" dataKey="consistency" stroke="#8B5CF6" strokeWidth={2} />
              <Line type="monotone" dataKey="timeliness" stroke="#F59E0B" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Insight Velocity */}
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
            <Brain className="w-5 h-5 text-purple-400" />
            <span>Insight Generation & Impact</span>
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={insightVelocityData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="month" stroke="#9CA3AF" />
              <YAxis stroke="#9CA3AF" />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1F2937',
                  border: '1px solid #374151',
                  borderRadius: '8px',
                  color: '#F9FAFB'
                }}
              />
              <Area type="monotone" dataKey="generated" stackId="1" stroke="#8B5CF6" fill="#8B5CF6" fillOpacity={0.6} />
              <Area type="monotone" dataKey="validated" stackId="2" stroke="#3B82F6" fill="#3B82F6" fillOpacity={0.6} />
              <Area type="monotone" dataKey="acted_upon" stackId="3" stroke="#10B981" fill="#10B981" fillOpacity={0.6} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Data Sources & Model Performance */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Data Sources Health */}
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
            <Layers className="w-5 h-5 text-blue-400" />
            <span>Data Sources Health</span>
          </h3>
          <div className="space-y-4">
            {dataSourcesData.map((source, index) => (
              <div key={index} className="bg-gray-700 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-white font-medium">{source.source}</h4>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-400">{source.volume}TB/day</span>
                    <div className={`w-3 h-3 rounded-full ${source.quality >= 95 ? 'bg-green-500' : source.quality >= 90 ? 'bg-yellow-500' : 'bg-red-500'}`}></div>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3 text-sm">
                  <div>
                    <div className="text-gray-400">Quality</div>
                    <div className="text-white font-medium">{source.quality}%</div>
                  </div>
                  <div>
                    <div className="text-gray-400">Latency</div>
                    <div className="text-white font-medium">{source.latency}s</div>
                  </div>
                  <div>
                    <div className="text-gray-400">Coverage</div>
                    <div className="text-white font-medium">{source.coverage}%</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ML Model Performance */}
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
            <Cpu className="w-5 h-5 text-pink-400" />
            <span>ML Model Performance</span>
          </h3>
          <div className="space-y-4">
            {modelPerformanceData.map((model, index) => (
              <div key={index} className="bg-gray-700 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-white font-medium">{model.model}</h4>
                  <div className="flex items-center space-x-2">
                    <span className={`text-sm ${model.drift < 3 ? 'text-green-400' : model.drift < 5 ? 'text-yellow-400' : 'text-red-400'}`}>
                      {model.drift}% drift
                    </span>
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-2 text-sm">
                  <div>
                    <div className="text-gray-400">Accuracy</div>
                    <div className="text-white font-medium">{model.accuracy}%</div>
                  </div>
                  <div>
                    <div className="text-gray-400">Precision</div>
                    <div className="text-white font-medium">{model.precision}%</div>
                  </div>
                  <div>
                    <div className="text-gray-400">Recall</div>
                    <div className="text-white font-medium">{model.recall}%</div>
                  </div>
                  <div>
                    <div className="text-gray-400">F1 Score</div>
                    <div className="text-white font-medium">{model.f1_score}%</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Data Pipeline Health */}
      <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
          <GitBranch className="w-5 h-5 text-green-400" />
          <span>Data Pipeline Health</span>
        </h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
          {pipelineHealth.map((pipeline, index) => (
            <div key={index} className="bg-gray-700 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-white font-medium">{pipeline.pipeline}</h4>
                <div className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs border ${getStatusColor(pipeline.status)}`}>
                  {getStatusIcon(pipeline.status)}
                  <span className="capitalize">{pipeline.status}</span>
                </div>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-400">Throughput:</span>
                  <span className="text-white">{pipeline.throughput}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Latency:</span>
                  <span className="text-white">{pipeline.latency}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Error Rate:</span>
                  <span className={`${pipeline.errors < 0.05 ? 'text-green-400' : pipeline.errors < 0.1 ? 'text-yellow-400' : 'text-red-400'}`}>
                    {pipeline.errors}%
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Analytics Usage & Data Governance */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <div className="flex items-center space-x-3 mb-4">
            <Eye className="w-6 h-6 text-blue-400" />
            <h3 className="text-lg font-semibold text-white">Dashboard Views</h3>
          </div>
          <div className="text-3xl font-bold text-white mb-2">24.8K</div>
          <div className="text-sm text-green-400">+15.2% vs last week</div>
        </div>

        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <div className="flex items-center space-x-3 mb-4">
            <Search className="w-6 h-6 text-green-400" />
            <h3 className="text-lg font-semibold text-white">Data Queries</h3>
          </div>
          <div className="text-3xl font-bold text-white mb-2">156K</div>
          <div className="text-sm text-green-400">+8.7% vs last week</div>
        </div>

        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <div className="flex items-center space-x-3 mb-4">
            <Filter className="w-6 h-6 text-purple-400" />
            <h3 className="text-lg font-semibold text-white">Active Users</h3>
          </div>
          <div className="text-3xl font-bold text-white mb-2">1.2K</div>
          <div className="text-sm text-green-400">+12.1% vs last week</div>
        </div>

        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <div className="flex items-center space-x-3 mb-4">
            <Zap className="w-6 h-6 text-yellow-400" />
            <h3 className="text-lg font-semibold text-white">API Calls</h3>
          </div>
          <div className="text-3xl font-bold text-white mb-2">2.4M</div>
          <div className="text-sm text-green-400">+22.3% vs last week</div>
        </div>
      </div>
    </div>
  );
};

export default DataDashboard;